import { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FormInput from '../../components/forms/formInput';
import UserRoleSelect from '../../components/selects/userRoleSelect';
import { createUser, uploadUserAvatar } from '../../services/companyUsers';
import { updateUser } from '../../services/users';
import AvatarUpload from '../../components/avatarUpload';
import { useMutation, useQueryClient } from 'react-query';
import { onMutateSuccess } from '../../utils/queryResponse';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const UserShieldIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M18 3l3 1.5V8a3 3 0 0 1-3 3 3 3 0 0 1-3-3V4.5L18 3z" /></svg>
);

// No username (derived from the e-mail by the API) and no password (the
// invitee chooses one through the e-mailed invitation link).
const initUser = {
    fullName: '',
    email: '',
    role: ''
}

const SetUserModal = ({
    display,
    toggle,
    selectedUser
}) => {
    const { t } = useTranslation();

    const [user, setUser] = useState(initUser)
    const [pendingAvatar, setPendingAvatar] = useState(null)   // file picked before a new user exists
    const [uploadingAvatar, setUploadingAvatar] = useState(false)
    const [openSeq, setOpenSeq] = useState(0)                  // remounts the avatar picker on each open

    const isEdit = !!selectedUser?.id

    const onShow = () => {
        if (selectedUser?.id) {
            // `roles` is the API's comma-joined role keys; a user has one role
            // per company so the first one is the editable value.
            const role = String(selectedUser.roles ?? '').split(',')[0].trim().toLowerCase()
            setUser({
                id: selectedUser.id,
                fullName: selectedUser.fullName ?? '',
                email: selectedUser.email ?? '',
                avatarUrl: selectedUser.avatarUrl,
                role,
            })
        } else {
            setUser(initUser)
        }
        setPendingAvatar(null)
        setOpenSeq((s) => s + 1)
    }

    const onHide = () => {
        toggle(false)
    }

    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation(isEdit ? updateUser : createUser, {
        onSuccess: onMutateSuccess(async (response) => {
            // New user: now that we have an id, upload the photo that was picked
            // on the form (deferred because the avatar endpoint needs the id).
            if (!isEdit && pendingAvatar && response?.id) {
                setUploadingAvatar(true)
                try { await uploadUserAvatar(response.id, pendingAvatar) } catch (e) { /* non-fatal */ }
                setUploadingAvatar(false)
            }
            queryClient.invalidateQueries(['users'])
            // The assignee pickers and the job activity timeline read the same
            // photo, so refresh their cached options too.
            queryClient.invalidateQueries(['users-option'])
            setPendingAvatar(null)
            onHide()
        }, isEdit ? undefined : t('settingsPage.invitationSentSetPassword'))
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const { id, fullName, email, role } = user
        mutate(isEdit ? { id, fullName, email, role } : { fullName, email, role })
    }

    const handleChange = (objValue) => {
        const newUser = { ...user, ...objValue }
        setUser(newUser)
    }

    const handleSelectChange = (prop) => (val) => {
        handleChange({ ...user, [prop]: val })
    }

    // Existing user: upload straight away, so the new photo is live even if the
    // admin closes the drawer without saving the rest of the form.
    const handleAvatar = async (file) => {
        if (!selectedUser?.id) return
        const res = await uploadUserAvatar(selectedUser.id, file)
        if (res?.code === 200 && res.message?.avatarUrl) {
            setUser((u) => ({ ...u, avatarUrl: res.message.avatarUrl }))
            queryClient.invalidateQueries(['users'])
            queryClient.invalidateQueries(['users-option'])
        } else {
            throw new Error(t('avatarUpload.uploadFailed'))
        }
    }

    return (
        <>
            <SbrDrawer
                show={display}
                onHide={onHide}
                onShow={onShow}
                secondary
                backdrop={(isLoading || uploadingAvatar) ? 'static' : true}
                title={isEdit ? t('settingsPage.editUserTitle') : t('settingsPage.inviteUserTitle')}
                subtitle={isEdit ? t('settingsPage.editUserSubtitle') : t('settingsPage.inviteUserSubtitle')}
                icon={<UserShieldIcon />}
                footer={
                    <>
                        <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading || uploadingAvatar}>{t('common.cancel')}</button>
                        <button type='submit' form="set-user" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading || uploadingAvatar}>
                            {isLoading ? t('settingsPage.submitting') : uploadingAvatar ? t('settingsPage.uploadingPhoto') : (isEdit ? t('settingsPage.saveUser') : t('settingsPage.sendInvitation'))}
                        </button>
                    </>
                }
            >
                <Form id='set-user' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                    {/* Employee photo. The same picture is what the job activity
                        timeline, the assignee pickers and this user list all
                        render, with initials as the fallback until one is set —
                        and an admin setting it here is the only route for staff
                        who never open their own Settings page. */}
                    <div className="mb-4 pb-3" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                        <AvatarUpload
                            key={openSeq}
                            size={66}
                            name={user.fullName}
                            src={user.avatarUrl}
                            onUpload={isEdit ? handleAvatar : undefined}
                            onSelect={isEdit ? undefined : setPendingAvatar}
                            disabled={isLoading || uploadingAvatar}
                        />
                        {!isEdit && pendingAvatar &&
                            <div className="sbr-avatarup-hint" style={{ marginTop: 8 }}>{t('settingsPage.photoSavedWithUser')}</div>
                        }
                    </div>

                    <Row className="mb-3">
                        <Col lg="6">
                            <FormInput className="form-control mb-2" label={t('settingsPage.fullName')} value={user.fullName ?? ''}
                                onChange={(e) => handleChange({ fullName: e.target.value })} required
                            />
                        </Col>
                        <Col lg="6">
                            <FormInput type="email" className="form-control mb-2" label={t('settingsPage.email2')} value={user.email ?? ''}
                                onChange={(e) => handleChange({ email: e.target.value })} required
                            />
                        </Col>
                        <Col lg="6">
                            <UserRoleSelect className="mb-2" label={t('settingsPage.role')} value={user.role ?? ''}
                                onChange={handleSelectChange('role')}
                                placeholder={t('settingsPage.selectRole')}
                                required
                            />
                        </Col>
                    </Row>

                    {!isEdit && (
                        <div style={{ fontSize: 12.5, color: 'var(--ink-soft, #64748B)', lineHeight: 1.55 }}>
                            {t('settingsPage.inviteHint')}
                        </div>
                    )}
                </Form>
            </SbrDrawer>
        </>
    )
}

export default SetUserModal
